// 销售单据共享领域逻辑：行项目金额/折扣/IVA 实时计算 + 状态机（前端镜像，服务端仍强制校验）。
import type { Tone } from './format';
import type { ContractStatus, DeliveryStatus, QuotationStatus } from './types';

// ---- 数值工具 ----
export function toNum(v: string | number | null | undefined): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ---- 行项目编辑态（表单里的原始字符串值，避免受控输入 NaN） ----
export interface SalesLineForm {
  key: string;
  item_id?: string | null;
  description_es: string;
  quantity: string;
  uom: string;
  list_price: string;      // 原价 / 目录价（USD）
  discount_percentage: string; // 折扣百分比 0-100
  discount_amount: string;     // 折扣金额（整行）
  unit_price: string;          // 成交净单价（真正入库、用于合计）
  tax_rate: string;            // IVA 百分比
  manual_price: boolean;       // 用户是否手动改过净单价（改后不再被折扣反算覆盖）
  price_override_reason?: string;
  discount_rule_name?: string;
}

export function emptyLine(key: string): SalesLineForm {
  return {
    key, item_id: null, description_es: '', quantity: '1', uom: 'Unidad',
    list_price: '0', discount_percentage: '0', discount_amount: '0', unit_price: '0',
    tax_rate: '21', manual_price: false,
  };
}

// 由原价 + 折扣（百分比 + 整行金额）反算成交净单价。
export function deriveUnitPrice(l: Pick<SalesLineForm, 'list_price' | 'discount_percentage' | 'discount_amount' | 'quantity'>): number {
  const list = toNum(l.list_price);
  const pct = Math.min(100, Math.max(0, toNum(l.discount_percentage)));
  const amt = Math.max(0, toNum(l.discount_amount));
  const qty = toNum(l.quantity);
  const perUnitDiscount = qty > 0 ? amt / qty : 0;
  return round2(list * (1 - pct / 100) - perUnitDiscount);
}

export interface LineComputed {
  quantity: number;
  unitPrice: number;
  lineTotal: number; // net = quantity * unit_price（与后端一致）
  taxRate: number;
  taxAmount: number;
  total: number;     // lineTotal + taxAmount
}

export function computeLine(l: SalesLineForm): LineComputed {
  const quantity = toNum(l.quantity);
  const unitPrice = round2(toNum(l.unit_price));
  const taxRate = toNum(l.tax_rate);
  const lineTotal = round2(quantity * unitPrice);
  const taxAmount = round2((lineTotal * taxRate) / 100);
  return { quantity, unitPrice, lineTotal, taxRate, taxAmount, total: round2(lineTotal + taxAmount) };
}

export interface DocTotals {
  subtotal: number;
  tax_total: number;
  grand_total: number;
}
export function computeTotals(lines: SalesLineForm[]): DocTotals {
  let subtotal = 0;
  let tax = 0;
  for (const l of lines) {
    const c = computeLine(l);
    subtotal += c.lineTotal;
    tax += c.taxAmount;
  }
  return { subtotal: round2(subtotal), tax_total: round2(tax), grand_total: round2(subtotal + tax) };
}

// 把编辑态行序列化成后端 createQuotation / updateQuotation 需要的载荷。
export function lineToPayload(l: SalesLineForm) {
  return {
    item_id: l.item_id || null,
    description_es: l.description_es,
    quantity: toNum(l.quantity),
    uom: l.uom || 'Unidad',
    unit_price: round2(toNum(l.unit_price)),
    tax_rate: toNum(l.tax_rate),
    list_price: toNum(l.list_price),
    discount_percentage: Math.min(100, Math.max(0, toNum(l.discount_percentage))),
    discount_amount: Math.max(0, toNum(l.discount_amount)),
  };
}

// 从后端返回的行回填编辑态。
export function lineFromRecord(l: {
  item_id?: string | null; description_es: string; quantity: number; uom: string;
  unit_price: number; tax_rate: number; list_price?: number | null;
  discount_percentage?: number; discount_amount?: number;
  discount_rule_snapshot?: {rule_name:string;overridden?:boolean;reason?:string} | null;
}, key: string): SalesLineForm {
  const hasList = l.list_price != null && l.list_price > 0 && Number(l.list_price) > Number(l.unit_price);
  return {
    key, item_id: l.item_id || null, description_es: l.description_es ?? '',
    quantity: String(l.quantity), uom: l.uom ?? 'Unidad',
    list_price: String(l.list_price ?? l.unit_price),
    discount_percentage: String(l.discount_percentage ?? 0),
    discount_amount: String(l.discount_amount ?? 0),
    unit_price: String(l.unit_price),
    tax_rate: String(l.tax_rate ?? 21),
    manual_price: l.discount_rule_snapshot ? !!l.discount_rule_snapshot.overridden : !hasList,
    discount_rule_name: l.discount_rule_snapshot?.rule_name,
    price_override_reason: l.discount_rule_snapshot?.reason,
  };
}

// ---- 报价单状态机（前端镜像）----
// 仅用于决定展示哪些流转按钮；角色权限与非法流转最终由 core-api 强制（403 / 409），
// 前端负责把后端错误原文回显给用户。confirm=true 表示不可逆或重要操作，需弹窗二次确认。
export interface QuotationAction {
  to: QuotationStatus;
  label: string;
  tone: Tone;
  confirm: boolean;
  adminOnly: boolean;
  primary?: boolean;
}

export const QUOTATION_ACTIONS: Record<QuotationStatus, QuotationAction[]> = {
  draft: [
    { to: 'sent', label: '发送客户', tone: 'info', confirm: false, adminOnly: false, primary: true },
    { to: 'rejected', label: '标记作废', tone: 'bad', confirm: true, adminOnly: false },
    { to: 'expired', label: '标记过期', tone: 'neutral', confirm: true, adminOnly: false },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  sent: [
    { to: 'accepted', label: '接受报价', tone: 'ok', confirm: true, adminOnly: true, primary: true },
    { to: 'rejected', label: '拒绝', tone: 'bad', confirm: true, adminOnly: false },
    { to: 'expired', label: '标记过期', tone: 'neutral', confirm: true, adminOnly: false },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  accepted: [
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  rejected: [],
  expired: [],
  cancelled: [],
};

// 报价单是否仍可编辑（草稿态；后端同样强制）。
export function quotationEditable(status: string): boolean {
  return status === 'draft';
}

// ---- 销售合同状态机（前端镜像）----
// draft→confirmed(签订)→in_production→ready→partially_delivered/completed；cancelled 自 2026-10-16
// 起下放业务操作员（与后端 STATUS_FLOWS 的 cancelled=BIZ_ROLES 同口径；原因必填 + 审计 + 业务校验不变）。
// completed / cancelled 的最终守卫（是否已全部交货、是否已有交货单）仍由 core-api 强制（409），
// 前端只决定按钮可见性并回显后端错误。confirm=true 用于签订/完工/取消等重要或不可逆操作。
export interface ContractAction {
  to: ContractStatus;
  label: string;
  tone: Tone;
  confirm: boolean;
  adminOnly: boolean;
  primary?: boolean;
}

export const CONTRACT_ACTIONS: Record<ContractStatus, ContractAction[]> = {
  draft: [
    { to: 'confirmed', label: '确认合同', tone: 'ok', confirm: true, adminOnly: false, primary: true },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  confirmed: [
    { to: 'in_production', label: '进入生产', tone: 'warn', confirm: false, adminOnly: false, primary: true },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  in_production: [
    { to: 'ready', label: '标记待发货', tone: 'info', confirm: false, adminOnly: false, primary: true },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  ready: [
    { to: 'partially_delivered', label: '标记部分交货', tone: 'warn', confirm: false, adminOnly: false },
    { to: 'completed', label: '标记完成', tone: 'ok', confirm: true, adminOnly: false, primary: true },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  partially_delivered: [
    { to: 'completed', label: '标记完成', tone: 'ok', confirm: true, adminOnly: false, primary: true },
    { to: 'cancelled', label: '取消', tone: 'bad', confirm: true, adminOnly: false },
  ],
  completed: [],
  // 2026-10-27 业务口径（用户明确）：「销售订单取消后应该可以再确认，因为有的时候
  //   客户说还没定，所以工程师取消，而现在客户说确认了就应该可以再确认。」
  // 后端已放开 cancelled → confirmed（并有「已交货/已完工则拒绝复活」守卫），
  // 前端同步给出按钮，否则界面上看不到入口（此前 cancelled: [] 导致无处可点）。
  cancelled: [
    { to: 'confirmed', label: '重新确认', tone: 'ok', confirm: true, adminOnly: false, primary: true },
  ],
};

// 合同是否仍可编辑（草稿态；后端同样强制：confirmed 起为合同正本，只能靠状态流转与交货单推进）。
export function contractEditable(status: string): boolean {
  return status === 'draft';
}

// ---- 交货单状态机（前端镜像）----
// 关键语义：真正的库存扣减与合同「已交数量 / 进度」回写只发生在「确认」这一步
// （草稿→确认，或即时确认新建）；已发出 / 已送达只是物流状态标签，不再动库存。
// 取消（非草稿）会回补出库库存并回退合同逐行已交数量；草稿取消无副作用。
// 最终合法性、超交 / 重复交付、角色权限均由 core-api 强制（403 / 409），前端只决定按钮可见性并回显错误。
export interface DeliveryAction {
  to: DeliveryStatus;
  label: string;
  tone: Tone;
  confirm: boolean;
  adminOnly: boolean;
  primary?: boolean;
  hint?: string;
}

export const DELIVERY_ACTIONS: Record<DeliveryStatus, DeliveryAction[]> = {
  draft: [
    { to: 'confirmed', label: '确认交货（出库并回写合同）', tone: 'ok', confirm: true, adminOnly: false, primary: true,
      hint: '确认将立即生成出库流水、扣减库存，并按合同行累加「已交数量」与推进合同进度（部分交货 / 全部完成）。此步会真正影响库存与合同。' },
    { to: 'cancelled', label: '取消草稿', tone: 'bad', confirm: true, adminOnly: false,
      hint: '草稿取消不会动库存，也不影响合同已交数量。' },
  ],
  confirmed: [
    { to: 'dispatched', label: '标记已发出', tone: 'warn', confirm: false, adminOnly: false, primary: true,
      hint: '物流状态推进（货物已发出），不重复扣库存。' },
    { to: 'cancelled', label: '取消交货', tone: 'bad', confirm: true, adminOnly: false,
      hint: '取消将回补出库库存并回退合同逐行已交数量与进度。' },
  ],
  dispatched: [
    { to: 'delivered', label: '标记已送达', tone: 'ok', confirm: true, adminOnly: false, primary: true,
      hint: '物流状态推进（客户已签收），不重复扣库存。' },
    { to: 'cancelled', label: '取消交货', tone: 'bad', confirm: true, adminOnly: false,
      hint: '取消将回补出库库存并回退合同逐行已交数量与进度。' },
  ],
  delivered: [],
  cancelled: [],
};

// 交货单是否仍可编辑（仅草稿；后端同样强制，历史 ERPNext 交货单永远只读）。
export function deliveryEditable(status: string): boolean {
  return status === 'draft';
}

// 可继续生成交货单的合同状态（草稿 / 已取消的合同不应再交货）。
export const DELIVERABLE_CONTRACT_STATUSES: ContractStatus[] = [
  'confirmed', 'in_production', 'ready', 'partially_delivered',
];
